import { type ChatChoices, ChatInputKind, type ChatResponse } from '@/types/generated/chat_api'
import { type Refusal, RefusalExpectedInput, RefusalReason } from '@/types/generated/public_api'

/**
 * What a clarifying question asks for, which picks the option UI: rich cards for instruments
 * and positions, chips for everything else.
 */
export type ChoiceKind =
  | 'instrument'
  | 'position'
  | 'duration'
  | 'price'
  | 'quantity'
  | 'percent'
  | 'date'
  | 'generic'

/** One answer: its button text and the prompt sent back when it is chosen. */
export interface ChoiceOption {
  label: string
  reply: string
}

/** A question the trading backend asked instead of guessing, with its known answers. */
export interface ChoiceRequest {
  kind: ChoiceKind
  options: ChoiceOption[]
  multiSelect: boolean
  /** The format of a typed answer; absent: the options are the only structured answers. */
  input?: { kind: ChatInputKind; placeholder: string }
}

const KIND_BY_EXPECTED_INPUT: Partial<Record<RefusalExpectedInput, ChoiceKind>> = {
  [RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_INSTRUMENT_SYMBOL]: 'instrument',
  [RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_POSITION_SYMBOL]: 'position',
  [RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_DURATION_SECONDS]: 'duration',
  [RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_PRICE]: 'price',
  [RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_QUANTITY]: 'quantity',
  [RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_PERCENT]: 'percent',
  [RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_DATE]: 'date',
}

const KIND_BY_REASON: Partial<Record<RefusalReason, ChoiceKind>> = {
  [RefusalReason.REFUSAL_REASON_AMBIGUOUS_INSTRUMENT]: 'instrument',
  [RefusalReason.REFUSAL_REASON_AMBIGUOUS_POSITION]: 'position',
}

// ChatInputKind folds instrument and position symbols into one SYMBOL kind, so it only decides
// when no refusal came with the answers.
const KIND_BY_INPUT: Partial<Record<ChatInputKind, ChoiceKind>> = {
  [ChatInputKind.CHAT_INPUT_KIND_SYMBOL]: 'instrument',
  [ChatInputKind.CHAT_INPUT_KIND_DURATION]: 'duration',
  [ChatInputKind.CHAT_INPUT_KIND_PRICE]: 'price',
  [ChatInputKind.CHAT_INPUT_KIND_QUANTITY]: 'quantity',
  [ChatInputKind.CHAT_INPUT_KIND_PERCENT]: 'percent',
  [ChatInputKind.CHAT_INPUT_KIND_DATE]: 'date',
}

function choiceKind(refusal: Refusal | undefined, choices: ChatChoices): ChoiceKind {
  if (refusal) {
    const kind = KIND_BY_EXPECTED_INPUT[refusal.expectedInput] ?? KIND_BY_REASON[refusal.reason]
    if (kind) return kind
    // An unit question ("5 coins or 5 USD") and anything else without a typed format.
    if (refusal.expectedInput === RefusalExpectedInput.REFUSAL_EXPECTED_INPUT_UNSPECIFIED) {
      return 'generic'
    }
  }
  return (choices.input && KIND_BY_INPUT[choices.input.kind]) ?? 'generic'
}

/**
 * The question a reply asks, from the `choices` chat_api relays (the answers and the typed
 * format) and the refusal they were drawn from (which tells an instrument from a position).
 * chat_api sets `choices` only when the plan asks exactly one question, so the first refusal is
 * that question's. Null when there is nothing to pick or type.
 */
export function choiceRequestFromResponse(response: ChatResponse): ChoiceRequest | null {
  const choices = response.choices
  if (!choices) return null
  const options = choices.options.filter((option) => option.reply)
  const input =
    choices.input && choices.input.kind !== ChatInputKind.CHAT_INPUT_KIND_UNSPECIFIED
      ? { kind: choices.input.kind, placeholder: choices.input.placeholder }
      : undefined
  if (options.length === 0 && !input) return null
  return {
    kind: choiceKind(response.refusals[0], choices),
    options,
    multiSelect: choices.multiSelect,
    input,
  }
}

/** The prompt a multi-select answer sends: the chosen replies in option order, joined by ", ". */
export function joinSelectedReplies(options: ChoiceOption[], selected: ReadonlySet<string>) {
  return options
    .filter((option) => selected.has(option.reply))
    .map((option) => option.reply)
    .join(', ')
}

const SYMBOL = /^[A-Za-z0-9][A-Za-z0-9._:/-]*$/
const DECIMAL = /^\d+(\.\d+)?$/

function positiveDecimal(text: string): string | null {
  return DECIMAL.test(text) && Number(text) > 0 ? text : null
}

/**
 * A typed answer in the format chat_api asked for (chat_api.proto ChatInputKind), trimmed, or
 * null when it isn't one — so a malformed answer never costs a round trip.
 */
export function normalizeTypedAnswer(
  kind: ChatInputKind,
  raw: string,
  multiSelect = false
): string | null {
  const text = raw.trim()
  if (!text) return null
  switch (kind) {
    case ChatInputKind.CHAT_INPUT_KIND_SYMBOL: {
      const symbols = multiSelect ? text.split(',').map((s) => s.trim()) : [text]
      return symbols.every((s) => SYMBOL.test(s)) ? symbols.join(', ') : null
    }
    case ChatInputKind.CHAT_INPUT_KIND_DURATION: {
      const match = /^(\d+)\s*([smh])$/i.exec(text)
      return match && Number(match[1]) > 0 ? `${Number(match[1])}${match[2].toLowerCase()}` : null
    }
    case ChatInputKind.CHAT_INPUT_KIND_PRICE:
    case ChatInputKind.CHAT_INPUT_KIND_QUANTITY:
      return positiveDecimal(text)
    case ChatInputKind.CHAT_INPUT_KIND_PERCENT: {
      const number = text.endsWith('%') ? text.slice(0, -1).trim() : text
      return DECIMAL.test(number) && Number(number) > 0 && Number(number) <= 100 ? text : null
    }
    case ChatInputKind.CHAT_INPUT_KIND_DATE: {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null
      const date = new Date(`${text}T00:00:00Z`)
      return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(text) ? text : null
    }
    default:
      return text
  }
}

// Expiries are UTC dates, so "today" is the UTC day for every viewer, not their local one.
function utcToday(now: number) {
  const at = new Date(now)
  return { year: at.getUTCFullYear(), month: at.getUTCMonth(), day: at.getUTCDate() }
}

/** Whole days from `now`'s UTC day to a YYYY-MM-DD (UTC) date, or null when it isn't one. */
export function daysUntil(date: string, now: number): number | null {
  const at = Date.parse(`${date}T00:00:00Z`)
  if (Number.isNaN(at)) return null
  const { year, month, day } = utcToday(now)
  return Math.round((at - Date.UTC(year, month, day)) / 86_400_000)
}

/** A calendar day (local midnight, as the calendar hands it over) as the YYYY-MM-DD reply. */
export function isoDateOf(day: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
}

/**
 * The earliest day a date answer may name: the UTC today, as the local-midnight Date the
 * calendar works in, so a day already past in UTC is disabled even where it is still local today.
 */
export function firstPickableDay(now: number): Date {
  const { year, month, day } = utcToday(now)
  return new Date(year, month, day)
}

/** Whether a calendar day may still be sent at `now`: a picker can stay open past midnight. */
export function isPickableDay(day: Date, now: number): boolean {
  return isoDateOf(day) >= isoDateOf(firstPickableDay(now))
}

/** The calendar days of the options that are YYYY-MM-DD dates, so the calendar can mark them. */
export function optionDates(options: ChoiceOption[]): Date[] {
  return options
    .map((option) => option.reply)
    .filter((reply) => normalizeTypedAnswer(ChatInputKind.CHAT_INPUT_KIND_DATE, reply) === reply)
    .map((reply) => {
      const [year, month, day] = reply.split('-').map(Number)
      return new Date(year, month - 1, day)
    })
}
